// Docs : https://geo.api.gouv.fr/adresse
import {
  faMapMarkerAltSlash, faSearch, faSpinner, faTimes
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Chip, ClickAwayListener, Divider, Grid, IconButton,
  InputAdornment, TextField, Typography
} from '@mui/material';
import { Question, SelectCountry } from 'components';
import { FormLine } from 'components/Intervention';
import { useStores } from 'hooks';
import { AddressView } from 'models';
import React, {
  Fragment, useCallback, useEffect, useState, useRef
} from 'react';
import {
 Control, Controller, UseFormWatch, UseFormSetValue, UseFormGetValues
} from 'react-hook-form';
import { AddressService } from 'services';
import styled from 'styled-components';
import { FormHelper, translate } from 'utils/helpers';

const AutocompleteContainer = styled.section`
  position: relative;
  z-index: 10;
`;

const AddressList = styled.ul`
  position: absolute;
  top: 56px;
  width: 100%;
  max-height: 250px;
  margin: 0;
  padding: 0;
  background: var(--white);
  border: 1px solid #eeeeee;
  overflow: auto;
`;

const AddressItem = styled.li`
  display: flex;
  align-items: center;
  transition: all 0.3s ease-in;
  padding: 1rem;
  cursor: pointer;

  &:hover, &:focus {
    background-color: #eeeeee;
  }
`;

const ENTER_KEYCODE = 13;

interface FormAddressProps {
  control: Control<any>;
  getValues: UseFormGetValues<any>;
  isRequired?: boolean;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  withCountry?: boolean;
}

export const FormAddress = ({
                              control,
                              watch,
                              setValue,
                              getValues,
                              isRequired = false,
                              withCountry = false
                            }: FormAddressProps) => {
  const { i18nStore } = useStores();
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [addressList, setAddressList] = useState([]);
  const [addressListIsVisible, setAddressListIsVisible] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(new AddressView());
  const debouncedQuery = FormHelper.useDebounce(query, 500);

  // Watch form values
  const watchedValues = watch();
  const address1 = watch('address1');
  const address2 = watch('address2');
  const postalCode = watch('postalCode');
  const city = watch('city');
  const country = watch('country');
  const addressCountry = watch('address.country');

  useEffect(() => {
    const getAddressList = async () => {
      if (debouncedQuery) {
        setIsSearching(true);
        const countryCode = addressCountry?.countryCode || country?.countryCode || '';
        try {
          const resultsMapbox = await AddressService.getAddressListMapbox(
              debouncedQuery,
              countryCode,
              i18nStore.currentLanguage
          ).then(response => response.features);
          setAddressList(resultsMapbox);
          setAddressListIsVisible(true);
          setIsSearching(false);
        } catch {
          setIsSearching(false);
        }
      } else {
        setAddressList([]);
      }
    };

    getAddressList();
  }, [debouncedQuery, addressCountry?.countryCode, country?.countryCode, i18nStore.currentLanguage]);

  useEffect(() => {
    if (selectedAddress && selectedAddress.address1) {
      // Update all address fields at once
      setValue('address1', selectedAddress.address1);
      setValue('address2', selectedAddress.address2 || '');
      setValue('postalCode', selectedAddress.postalCode || '');
      setValue('city', selectedAddress.city || '');

      if (withCountry && selectedAddress.country) {
        setValue('country', selectedAddress.country);
        setValue('address', selectedAddress);
      } else {
        const currentValues = getValues();
        setValue('address', {
          ...selectedAddress,
          country: currentValues.address?.country || currentValues.country
        });
      }

      setAddressListIsVisible(false);
    }
  }, [selectedAddress, withCountry, setValue, getValues]);

  const handleChangeQuery = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  }, []);

  const handleClearQuery = useCallback(() => {
    setQuery('');
    setAddressList([]);
    setIsSearching(false);
  }, []);

  const handleShowAddressList = useCallback(() => {
    setAddressListIsVisible(true);
  }, []);

  const handleHideAddressList = useCallback(() => {
    setAddressListIsVisible(false);
  }, []);

  const handleSelectAddress = useCallback((address: any) => {
    const postcode = address.context.find((ctx: any) => ctx.id.includes('postcode'));
    const city = address.context.find((ctx: any) => ctx.id.includes('place'));
    const country = address.context.find((ctx: any) => ctx.id.includes('country'));

    const addressFormatted = {
      address1: (address.properties?.address ? address.properties.address : `${address.address || ''} ${address.text || ''}`).trim(),
      postalCode: postcode ? postcode.text : '',
      city: city ? city.text : '',
      country: country ? {
        value: country.short_code,
        countryCode: country.short_code,
        label: country.text
      } : getValues('country')
    };

    setSelectedAddress(addressFormatted);
    handleHideAddressList();
  }, [handleHideAddressList, getValues]);

  const handleSelectCountry = useCallback((value: any) => {
    const currentValues = getValues();
    setValue('address', {
      ...currentValues.address,
      country: value
    });
    setValue('country', value);
  }, [setValue, getValues]);

  // Helper function to check if field is required
  const isFieldRequired = useCallback((fieldName: string, dependentFields: string[]) => {
    if (isRequired) return true;
    const currentValues = getValues();
    const hasAnyDependentValue = dependentFields.some(field => {
      const fieldValue = field.includes('.')
          ? field.split('.').reduce((obj, key) => obj?.[key], currentValues)
          : currentValues[field];
      return fieldValue && fieldValue.toString().trim() !== '';
    });
    return hasAnyDependentValue;
  }, [isRequired, getValues]);

  return (
    <Question label={translate('common.address')}>
      <ClickAwayListener onClickAway={handleHideAddressList}>
        <AutocompleteContainer>
          <TextField
                autoComplete="new-password"
                fullWidth
                InputProps={{
                  endAdornment: query !== '' && (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={handleClearQuery}>
                      <FontAwesomeIcon color="var(--primary-color)" icon={faTimes} size="xs" />
                    </IconButton>
                  </InputAdornment>
                  ),
                  startAdornment: (
                    <InputAdornment position="start">
                      {isSearching
                            ? <FontAwesomeIcon icon={faSpinner} spin />
                            : <FontAwesomeIcon color="var(--primary-color)" icon={faSearch} />}
                    </InputAdornment>
                  )
                }}
                label={translate('common.addressAutocomplete')}
                value={query}
                variant="outlined"
                onChange={handleChangeQuery}
                onFocus={handleShowAddressList}
            />
          {addressListIsVisible && (
            <AddressList>
              {addressList.length > 0 && addressList.map((address: any) => (
                <Fragment key={address.id}>
                  <AddressItem
                            tabIndex={0}
                            onClick={() => handleSelectAddress(address)}
                            onKeyDown={e => e.keyCode === ENTER_KEYCODE && handleSelectAddress(address)}
                        >
                    <div style={{ minWidth: '120px' }}>
                      <Chip label={translate(`common.${address.place_type[0]}`)} />
                    </div>
                    <div>
                      <Typography style={{ fontWeight: 600 }}>{address.place_name}</Typography>
                    </div>
                  </AddressItem>
                  <Divider />
                </Fragment>
                  ))}
              {addressList.length === 0 && debouncedQuery && !isSearching && (
              <Typography align="center" style={{ padding: '2rem' }}>
                <FontAwesomeIcon icon={faMapMarkerAltSlash} size="2x" />
                <br />
                {translate('errors.noAddressList')}
              </Typography>
                  )}
            </AddressList>
            )}
        </AutocompleteContainer>
      </ClickAwayListener>

      <FormLine>
        <Grid container spacing={2}>
          <Grid item sm={6} xs={12}>
            <Controller
                  control={control}
                  name="address1"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                          {...field}
                          autoComplete="new-password"
                          error={!!error}
                          helperText={error?.message}
                          label={translate('common.address')}
                          required={isFieldRequired('address1', ['postalCode', 'city'])}
                      />
                  )}
                  rules={{
                    required: isFieldRequired('address1', ['postalCode', 'city'])
                        ? translate('errors.required')
                        : false
                  }}
              />
          </Grid>
          <Grid item sm={6} xs={12}>
            <Controller
                  control={control}
                  name="address2"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                          {...field}
                          autoComplete="new-password"
                          error={!!error}
                          helperText={error?.message}
                          label={translate('common.address2')}
                      />
                  )}
              />
          </Grid>
        </Grid>
      </FormLine>

      <FormLine>
        <Grid container>
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item sm={4} xs={12}>
                <Controller
                      control={control}
                      name="postalCode"
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!error}
                              helperText={error?.message}
                              label={translate('common.postalCode')}
                              required={isFieldRequired('postalCode', ['address1', 'city'])}
                          />
                      )}
                      rules={{
                        required: isFieldRequired('postalCode', ['address1', 'city'])
                            ? translate('errors.required')
                            : false
                      }}
                  />
              </Grid>
              <Grid item sm={8} xs={12}>
                <Controller
                      control={control}
                      name="city"
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!error}
                              helperText={error?.message}
                              label={translate('common.city')}
                              required={isFieldRequired('city', ['address1', 'postalCode'])}
                          />
                      )}
                      rules={{
                        required: isFieldRequired('city', ['address1', 'postalCode'])
                            ? translate('errors.required')
                            : false
                      }}
                  />
              </Grid>
            </Grid>
          </Grid>
          {withCountry && (
            <Grid item xs={12}>
              <SelectCountry
                      required={isFieldRequired('country', ['address1', 'postalCode', 'city'])}
                      value={country || null}
                      onChange={handleSelectCountry}
                  />
            </Grid>
            )}
        </Grid>
      </FormLine>
    </Question>
  );
};
